import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { vehicleFeeAPI, vehicleTypeAPI } from '../services/chalanService';
import { formatDate } from '../utils/formatDate';

function FeeManagement() {
  const { t } = useTranslation();
  const [feeStructures, setFeeStructures] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentFeeStructure, setCurrentFeeStructure] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_type: '',
    base_fee: '',
    description: '',
    is_active: true,
  });

  // Fetch fee structures and vehicle types
  const fetchFeeStructures = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vehicleFeeAPI.getFeeStructures();
      const data = response.data.results || response.data || [];
      setFeeStructures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching fee structures:', err);
      setError(t('feeManagement.errorLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleTypeAPI.getVehicleTypes();
      const types = response.data.results || response.data || [];
      setVehicleTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error('Error fetching vehicle types:', err);
    }
  };

  useEffect(() => {
    fetchFeeStructures();
    fetchVehicleTypes();
  }, []);

  const handleOpenDialog = (feeStructure = null) => {
    if (feeStructure) {
      setIsEditing(true);
      setCurrentFeeStructure(feeStructure);
      setFormData({
        // vehicle_type is just the ID from the serializer
        vehicle_type: String(feeStructure.vehicle_type || ''),
        base_fee: feeStructure.base_fee,
        description: feeStructure.description || '',
        is_active: feeStructure.is_active || true,
      });
    } else {
      setIsEditing(false);
      setCurrentFeeStructure(null);
      setFormData({
        vehicle_type: '',
        base_fee: '',
        description: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      vehicle_type: '',
      base_fee: '',
      description: '',
      is_active: true,
    });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.vehicle_type) {
        setError(t('feeManagement.validationVehicleType'));
        setLoading(false);
        return;
      }

      if (!formData.base_fee || parseFloat(formData.base_fee) <= 0) {
        setError(t('feeManagement.validationBaseFee'));
        setLoading(false);
        return;
      }

      const submitData = {
        vehicle_type: parseInt(formData.vehicle_type),
        base_fee: parseFloat(formData.base_fee),
        description: formData.description,
        is_active: formData.is_active,
      };

      if (isEditing && currentFeeStructure) {
        // Update
        await vehicleFeeAPI.updateFeeStructure(
          currentFeeStructure.id,
          submitData
        );
        setSuccess(t('feeManagement.successUpdated'));
      } else {
        // Create
        await vehicleFeeAPI.createFeeStructure(submitData);
        setSuccess(t('feeManagement.successCreated'));
      }
      
      // Refetch the entire list to ensure all fields (including vehicle_type_name) are fresh
      await fetchFeeStructures();

      handleCloseDialog();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving fee structure:', err);
      setError(
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        t('feeManagement.errorSaveFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('feeManagement.confirmDelete'))) {
      try {
        setLoading(true);
        await vehicleFeeAPI.deleteFeeStructure(id);
        setFeeStructures((prev) => prev.filter((fs) => fs.id !== id));
        setSuccess(t('feeManagement.successDeleted'));
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('Error deleting fee structure:', err);
        setError(t('feeManagement.errorDeleteFailed'));
      } finally {
        setLoading(false);
      }
    }
  };

  const getVehicleTypeName = (feeStructure) => {
    // The serializer returns vehicle_type_name directly
    // Check if the property exists in the response (even if empty string)
    if (feeStructure && feeStructure.hasOwnProperty('vehicle_type_name')) {
      const name = feeStructure.vehicle_type_name;
      // Return the name if it's a non-empty string, otherwise 'Unknown'
      return (typeof name === 'string' && name.trim() !== '') ? name : t('common.unknown');
    }

    // Fallback: try to find by vehicle_type id from the loaded vehicleTypes array
    const vtId = feeStructure?.vehicle_type;
    if (vehicleTypes.length > 0 && vtId) {
      const vType = vehicleTypes.find((vt) => vt.id === vtId);
      if (vType) return vType.name;
    }
    return t('common.unknown');
  };

  return (
    <Box sx={{ padding: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {t('feeManagement.pageTitle')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('feeManagement.addFeeStructure')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ marginBottom: '20px' }}>{success}</Alert>}

      {/* Statistics Cards */}
      {feeStructures.length > 0 && (
        <Grid container spacing={2} sx={{ marginBottom: '30px' }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  {t('feeManagement.totalVehicles')}
                </Typography>
                <Typography variant="h5">
                  {feeStructures.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  {t('common.active')}
                </Typography>
                <Typography variant="h5" sx={{ color: '#4caf50' }}>
                  {feeStructures.filter((fs) => fs.is_active).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  {t('feeManagement.averageFee')}
                </Typography>
                <Typography variant="h5">
                  Rs. {(
                    feeStructures.reduce((sum, fs) => sum + parseFloat(fs.base_fee || 0), 0) /
                    feeStructures.length
                  ).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Loading */}
      {loading && !dialogOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('chalanList.vehicleType')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {t('feeManagement.baseFee')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitSearch.description')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitList.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('feeManagement.lastUpdated')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeStructures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: '40px' }}>
                    {t('feeManagement.noFeeStructures')}
                  </TableCell>
                </TableRow>
              ) : (
                feeStructures.map((fs) => (
                  <TableRow key={fs.id} hover>
                    <TableCell>{getVehicleTypeName(fs)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      Rs. {parseFloat(fs.base_fee).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {fs.description ? (
                        <Typography variant="body2" sx={{ maxWidth: '200px' }}>
                          {fs.description.substring(0, 50)}
                          {fs.description.length > 50 ? '...' : ''}
                        </Typography>
                      ) : (
                        t('permitSearch.na')
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={fs.is_active ? t('common.active') : t('feeManagement.inactiveLabel')}
                        color={fs.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(fs.updated_at)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: '5px' }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenDialog(fs)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => handleDelete(fs.id)}
                        >
                          {t('common.delete')}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? t('feeManagement.editFeeStructure') : t('feeManagement.addNewFeeStructure')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '20px' }}>
          <TextField
            fullWidth
            select
            label={t('feeManagement.vehicleTypeStar')}
            name="vehicle_type"
            value={formData.vehicle_type}
            onChange={handleFormChange}
            disabled={isEditing}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: '15px' }}
          >
            <option value="">{t('createChalan.selectVehicleType')}</option>
            {vehicleTypes
              .filter((vt) => {
                const currentVtId = parseInt(formData.vehicle_type, 10);
                // If editing, allow current vehicle type or types without fee structure
                if (isEditing) {
                  return (
                    vt.id === currentVtId ||
                    !feeStructures.some((fs) => fs.vehicle_type === vt.id)
                  );
                }
                // If creating, only show vehicle types without fee structure
                return !feeStructures.some((fs) => fs.vehicle_type === vt.id);
              })
              .map((vt) => (
                <option key={vt.id} value={vt.id}>
                  {vt.name}
                </option>
              ))}
          </TextField>

          <TextField
            fullWidth
            label={t('feeManagement.baseFeeStar')}
            name="base_fee"
            type="number"
            inputProps={{ step: '0.01' }}
            value={formData.base_fee}
            onChange={handleFormChange}
            sx={{ marginBottom: '15px' }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('permitSearch.description')}
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            placeholder={t('feeManagement.descriptionPlaceholder')}
            sx={{ marginBottom: '15px' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                name="is_active"
                checked={formData.is_active}
                onChange={handleFormChange}
              />
            }
            label={t('common.active')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {isEditing ? t('feeManagement.update') : t('feeManagement.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FeeManagement;
